import React, { useState, useEffect } from "react";
import axios from '../../utils/axiosInstance';
import { toastSuccess, toastError } from '../../utils/alertHelper';
import { useNavigate, useParams, Link } from "react-router-dom";

const initialValues = {
  name: "",
  email: "",
  mobile: "",
  gstNumber: "",
  address: "",
  area: "",
  city: "",
  state: "",
  country: "",
  pinCode: "",
  birthDate: "",
  anniversaryDate: "",
  leadSource: "",
  leadCreatedBy: "",
  leadOwner: "",
  dealStage: "",
  dealTypes: "",
  dealTemperature: "",
  referralName: "",
  referralNumber: "",
  lastReason: "",
  notes: "",
  projectType: "",
};

const LeadCreate = () => {
  const [values, setValues] = useState(initialValues);
  const [dropdowns, setDropdowns] = useState({
    projectType: [],
    leadSource: [],
    dealStage: [],
    dealType: [],
    dealTemperature: [],
    country: [],
    state: [],
    city: [],
    leadOwner: [],
  });
  const [currentUser, setCurrentUser] = useState({ firstName: "", lastName: "" });
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { id } = useParams(); // <--- get lead id from route params if editing
  const businessId = localStorage.getItem("businessId");

  // Fetch dropdowns and user info
  useEffect(() => {
    const fetchDropdowns = async () => {
      try {
        const businessId = localStorage.getItem("businessId");

        // fetch all dropdowns (same as your code)
        const [
          projectTypeRes,
          leadSourceRes,
          dealStageRes,
          dealTypeRes,
          dealTemperatureRes,
          countryRes,
          stateRes,
          cityRes,
          leadOwnerRes,
        ] = await Promise.all([
          axios.get(`/admin/api/project-type?business=${businessId}`),
          axios.get(`/admin/api/lead-source?business=${businessId}`),
          axios.get(`/admin/api/deal-stage?business=${businessId}`),
          axios.get(`/admin/api/deal-type?business=${businessId}`),
          axios.get(`/admin/api/deal-temprature?business=${businessId}`),
          axios.get(`/admin/api/country?business=${businessId}`),
          axios.get(`/admin/api/state?business=${businessId}`),
          axios.get(`/admin/api/city?business=${businessId}`),
          // Fetch only users of type Company for lead owner dropdown
          axios.get(`/api/user?business=${businessId}&userType=Company`),
        ]);
        setDropdowns(prev => ({
          ...prev,
          projectType: (projectTypeRes.data.projectTypes || []).map(item => ({ value: item._id, label: item.name })),
          leadSource: (leadSourceRes.data.leadSources || []).map(item => ({ value: item._id, label: item.name })),
          dealStage: (dealStageRes.data.dealStages || []).map(item => ({ value: item._id, label: item.name })),
          dealType: (dealTypeRes.data.dealTypes || []).map(item => ({ value: item._id, label: item.name })),
          dealTemperature: (dealTemperatureRes.data.dealTempratures || []).map(item => ({ value: item._id, label: item.name })),
          country: (countryRes.data.countries || []).map(item => ({ value: item._id, label: item.name })),
          state: (stateRes.data.states || []).map(item => ({ value: item._id, label: item.name })),
          city: (cityRes.data.cities || []).map(item => ({ value: item._id, label: item.name })),
          leadOwner: (leadOwnerRes.data.users || []).map(user => ({
            value: user._id,
            label: `${user.firstName || ""} ${user.lastName || ""}`.trim(),
          })),
        }));

        // Lead Created By: current logged-in user
        const userId = localStorage.getItem("userId");
        const firstName = localStorage.getItem("firstName") || "";
        const lastName = localStorage.getItem("lastName") || "";

        if (userId) {
          setCurrentUser({
            firstName: firstName,
            lastName: lastName,
          });
          setValues(v => ({
            ...v,
            leadCreatedBy: userId,
            business: localStorage.getItem("businessId") || ""
          }));
        }
      } catch (err) {
        toastError("Failed to fetch dropdowns. Please reload the page.");
        console.error("Dropdown fetch error", err);
      }
    };
    fetchDropdowns();
  }, []);

  // If editing, fetch lead details by id
  useEffect(() => {
    if (!id) return;
    setLoading(true);
    axios.get(`/admin/api/lead/${id}`)
      .then(res => {
        if (res.data.Status === 1) {
          const lead = res.data.lead;
          setValues({
            name: lead.name || "",
            email: lead.email || "",
            mobile: lead.mobile || "",
            gstNumber: lead.gst_number || "",
            address: lead.address || "",
            area: lead.area || "",
            city: lead.city?._id || "",
            state: lead.state?._id || "",
            country: lead.country?._id || "",
            pinCode: lead.pin_code || "",
            birthDate: lead.bithDate ? lead.bithDate.split('T')[0] : "",
            anniversaryDate: lead.anniversaryDate ? lead.anniversaryDate.split('T')[0] : "",
            leadSource: lead.leadSource?._id || "",
            leadCreatedBy: lead.createdBy?._id || "",
            leadOwner: lead.leadOwner?._id || "",
            dealStage: lead.dealStage?._id || "",
            dealTypes: lead.dealType?._id || "",
            dealTemperature: lead.dealTemprature?._id || "",
            referralName: lead.ReferralName || "",
            referralNumber: lead.ReferralNumber || "",
            lastReason: lead.lostReason || "",
            notes: lead.notes || "",
            projectType: lead.projectType?._id || "",
          });
        } else {
          toastError(res.data.Message || "Failed to fetch lead");
        }
      })
      .catch(err => {
        toastError("Failed to fetch lead");
        console.error(err);
      })
      .finally(() => setLoading(false));
  }, [id]);

  // Handle field change
  const handleChange = (e) => {
    const { name, value } = e.target;
    setValues((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Handle Save/Update
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const req = {
        name: values.name,
        email: values.email,
        mobile: values.mobile,
        phone_code: "91",
        iso_code: "IN",
        gst_number: values.gstNumber,
        bithDate: values.birthDate,
        anniversaryDate: values.anniversaryDate,
        address: values.address,
        area: values.area,
        city: values.city,
        state: values.state,
        country: values.country,
        pin_code: values.pinCode,
        ReferralName: values.referralName,
        ReferralNumber: values.referralNumber,
        lostReason: values.lastReason,
        business: localStorage.getItem("businessId") || "",
        notes: values.notes,
        createdBy: values.leadCreatedBy,
      };

      // Only add leadOwner if it has a value
      if (values.leadOwner && values.leadOwner.trim() !== "") {
        req.leadOwner = values.leadOwner;
      }

      if (values.projectType) req.projectType = values.projectType;
      if (values.leadSource) req.leadSource = values.leadSource;
      if (values.dealStage) req.dealStage = values.dealStage;
      if (values.dealTypes) req.dealType = values.dealTypes;
      if (values.dealTemperature) req.dealTemprature = values.dealTemperature;

      let response;
      if (id) {
        // Update lead (PUT or PATCH depending on backend)
        response = await axios.put(`/admin/api/lead/${id}`, req);
      } else {
        // Create lead
        response = await axios.post("/admin/api/lead", req);
      }
      if (response.data.Status === 1) {
        toastSuccess(id ? "Lead updated successfully!" : "Lead added successfully!");
        setValues(initialValues);
        localStorage.setItem("lastLead", JSON.stringify(response.data.lead));
        setTimeout(() => {
          navigate("/user/lead");
        }, 500);
      } else {
        toastError("Error saving lead! " + (response.data.Message || "Unknown error"));
      }
    } catch (error) {
      console.error(error.response?.data || error.message);
      toastError("Error saving lead! " + (error.response?.data?.message || error.message));
    }
  };

  const handleCancel = () => {
    setValues(initialValues);
    // Always go back to lead list on cancel
    navigate("/user/lead");
  };

  // Helper to check if Referral fields should show
  const isReferral = dropdowns.leadSource.some(
    option => option.value === values.leadSource && option.label.toLowerCase().includes("referral")
  );
  const isLost = dropdowns.dealStage.some(
    option => option.value === values.dealStage && option.label.toLowerCase() === "lost"
  );

  return (
    <div className="container py-4" style={{ maxWidth: 1200 }}>
      <div className="d-flex justify-content-between align-items-center mb-2">
        <h2 style={{ fontWeight: "bold" }}>{id ? 'Edit Lead' : 'Add New Lead'}</h2>
        <div>
          <Link to="/user/lead" className="text-decoration-none text-secondary">
            &larr; Lead
          </Link>
        </div>
      </div>
      <form
        className="lead-form rounded-3 p-3"
        style={{ background: "#f6f6f6", border: "1.5px solid #BBBFD2" }}
        onSubmit={handleSubmit}
      >
        <div className="row mb-2">
          <div className="col-md-3 mb-2">
            <label className="form-label">
              Name <span className="text-danger">*</span>
            </label>
            <input type="text" name="name" value={values.name} onChange={handleChange} required className="form-control rounded-3" />
          </div>
          <div className="col-md-3 mb-2">
            <label className="form-label">
              Email
            </label>
            <input type="text" name="email" value={values.email} onChange={handleChange} className="form-control rounded-3" />
          </div>
          <div className="col-md-3 mb-2">
            <label className="form-label">
              Mobile <span className="text-danger">*</span>
            </label>
            <input type="text" name="mobile" value={values.mobile} onChange={handleChange} required className="form-control rounded-3" />
          </div>
          <div className="col-md-3 mb-2">
            <label className="form-label">GST Number</label>
            <input type="text" name="gstNumber" value={values.gstNumber} onChange={handleChange} className="form-control rounded-3" />
          </div>
        </div>
        <hr className="mt-3 mb-0  border-secondary opacity-20" />


        <div className="row mb-2">
          <div className="col-md-6 mb-2">
            <label className="form-label mt-3">Address</label>
            <textarea name="address" value={values.address} onChange={handleChange} className="form-control rounded-3" />
          </div>
          <div className="col-md-3 mb-2">
            <label className="form-label mt-3">Area</label>
            <input type="text" name="area" value={values.area} onChange={handleChange} className="form-control rounded-3" />
          </div>
          <div className="col-md-3 mb-2">
            <label className="form-label mt-3">City</label>
            <select
              name="city"
              value={values.city}
              onChange={handleChange}
              className="form-select rounded-3"
            >
              <option value="">Select City</option>
              {dropdowns.city.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="row mb-3">
          <div className="col-md-3 mb-2">
            <label className="form-label mt-2">State</label>
            <select
              name="state"
              value={values.state}
              onChange={handleChange}
              className="form-select rounded-3"
            >
              <option value="">Select State</option>
              {dropdowns.state.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>


          {/* Country */}
          <div className="col-md-3 mb-3">
            <label className="form-label fw-semibold mt-2">Country</label>
            <select
              name="country"
              value={values.country}
              onChange={handleChange}
              className="form-select rounded-3 shadow-sm"
            >
              <option value="">Select Country</option>
              {dropdowns.country.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {/* Pin Code */}
          <div className="col-md-3 mb-3">
            <label className="form-label fw-semibold mt-2">Pin Code</label>
            <input
              type="text"
              name="pinCode"
              value={values.pinCode}
              onChange={handleChange}
              className="form-control rounded-3 shadow-sm"
              placeholder="Enter Pin Code"
            />
          </div>

          {/* Birth Date */}
          <div className="col-md-3 mb-3">
            <label className="form-label fw-semibold mt-2">Birth Date</label>
            <input
              type="date"
              name="birthDate"
              value={values.birthDate}
              onChange={handleChange}
              className="form-control rounded-3 shadow-sm"
            />
          </div>

          {/* Anniversary Date */}
          <div className="col-md-3 mb-3">
            <label className="form-label fw-semibold mt-2">Anniversary Date</label>
            <input
              type="date"
              name="anniversaryDate"
              value={values.anniversaryDate}
              onChange={handleChange}
              className="form-control rounded-3 shadow-sm"
            />
          </div>
        </div>
        <hr className="mt-3 mb-0  border-secondary opacity-20" />


        <div className="row mb-2">
          <div className="col-md-3 mb-2">
            <label className="form-label mt-3">Project Type</label>
            <select name="projectType" value={values.projectType} onChange={handleChange} className="form-select rounded-3 shadow-sm">
              <option value="">Select Project Type</option>
              {dropdowns.projectType.map(option => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </div>
          <div className="col-md-3 mb-2">
            <label className="form-label mt-3">Lead Source</label>
            <select name="leadSource" value={values.leadSource} onChange={handleChange} className="form-select rounded-3 shadow-sm">
              <option value="">Select Lead Source</option>
              {dropdowns.leadSource.map(option => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </div>
          <div className="col-md-3 mb-2">
            <label className="form-label mt-3">Lead Created By</label>
            <input
              type="text"
              name="leadCreatedBy"
              value={`${currentUser.firstName} ${currentUser.lastName}`.trim()}
              disabled
              className="form-control"
            />
          </div>
          <div className="col-md-3 mb-2">
            <label className="form-label mt-3">Lead Owner</label>
            <select name="leadOwner" value={values.leadOwner} onChange={handleChange} className="form-select rounded-3 shadow-sm">
              <option value="">Select Lead Owner</option>
              {dropdowns.leadOwner.map(option => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </div>
        </div>
        {/* Show Referral fields only if Lead Source is Referral */}
        {isReferral && (
          <div className="row mb-2">
            <div className="col-md-3 mb-2">
              <label className="form-label">Referral Name</label>
              <input type="text" name="referralName" value={values.referralName} onChange={handleChange} className="form-control" />
            </div>
            <div className="col-md-3 mb-2">
              <label className="form-label">Referral Number</label>
              <input type="text" name="referralNumber" value={values.referralNumber} onChange={handleChange} className="form-control" />
            </div>
          </div>
        )}
        <div className="row mb-2">
          <div className="col-md-3 mb-2">
            <label className="form-label">Deal Stage</label>
            <select name="dealStage" value={values.dealStage} onChange={handleChange} className="form-select rounded-3 shadow-sm">
              <option value="">Select Deal Stage</option>
              {dropdowns.dealStage.map(option => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </div>
          <div className="col-md-3 mb-2">
            <label className="form-label">Deal Types</label>
            <select name="dealTypes" value={values.dealTypes} onChange={handleChange} className="form-select rounded-3 shadow-sm">
              <option value="">Select Deal Types</option>
              {dropdowns.dealType.map(option => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </div>
          <div className="col-md-3 mb-2">
            <label className="form-label">Deal Temperature</label>
            <select name="dealTemperature" value={values.dealTemperature} onChange={handleChange} className="form-select rounded-3 shadow-sm">
              <option value="">Select Deal Temperature</option>
              {dropdowns.dealTemperature.map(option => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </div>
        </div>
        {/* Show Last Reason only if Deal Type is Lost */}
        {isLost && (
          <div className="row mb-2">
            <div className="col-md-6 mb-2">
              <label className="form-label">Last Reason</label>
              <textarea name="lastReason" value={values.lastReason} onChange={handleChange} className="form-control" />
            </div>
          </div>
        )}
        <div className="row mb-2">
          <div className="col-md-3 mb-2">
            <label className="form-label">Notes</label>
            <textarea name="notes" value={values.notes} onChange={handleChange} className="form-control rounded-3" />
          </div>
        </div>
        <div className="row mt-3">
          <div className="col-md-12 text-end">
            <button type="button" className="btn btn-secondary me-2 rounded-3" onClick={handleCancel}>Cancel</button>
            <button type="submit" className="btn btn-primary px-4 rounded-3">Save</button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default LeadCreate;